import React, { useState, useEffect, useContext, useRef } from "react";
import { API_URL } from "../components/api";
import "bootstrap/dist/css/bootstrap.css";
import Axios from "axios";
import AuthContext from "../context/AuthContext";

function IPTVSubDevices(props) {
  const { user } = useContext(AuthContext);
  const authTokens = JSON.parse(localStorage.getItem("authTokens"));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [newDeviceName, setNewDeviceName] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);
  const isMountedRef = useRef(true);

  const authHeaders = {
    Authorization: `Bearer ${authTokens?.access}`,
    "Content-Type": "application/json",
  };

  const formatDMY = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // Fetch devices for a given IPTV subscription
  const fetchDevices = async () => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const response = await Axios.get(
        `${API_URL}/radius/subscriptions/iptv/subscriptions/devices/`,
        {
          headers: authHeaders,
          params: {
            username: user?.username,
            iptv_id: props.iptv_id,
          },
        }
      );

      if (response.status === 200 && isMountedRef.current) {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data
          ? [response.data]
          : [];
        setRows(data);
      }
    } catch (err) {
      console.error(err);
      if (isMountedRef.current) setErrorMsg("Failed to load devices.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  // Add (POST) a device for this IPTV subscription
  const handleAddDevice = async (e) => {
    e?.preventDefault?.();
    const name = newDeviceName.trim();
    if (!name) return;

    try {
      setSubmitting(true);
      setErrorMsg(null);

      await Axios.post(
        `${API_URL}/radius/subscriptions/iptv/subscriptions/devices/`,
        {
          Action: "Post",
          device_name: name,
          iptv_subscription_id: props.iptv_id,
          userdealer: user?.username,
        },
        { headers: authHeaders }
      );

      setNewDeviceName("");
      await fetchDevices();
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to add device.");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete (PUT) a device by iptv_subscription_device_id
  const handleDelete = async (subscriptionDeviceId) => {
    if (!subscriptionDeviceId) return;
    try {
      setDeletingId(subscriptionDeviceId);
      setErrorMsg(null);

      await Axios.post(
        `${API_URL}/radius/subscriptions/iptv/subscriptions/devices/`,
        {
          Action: "Put",
          iptv_subscription_device_id: subscriptionDeviceId,
          // include if your backend expects them:
          iptv_subscription_id: props.iptv_id,
          userdealer: user?.username,
        },
        { headers: authHeaders }
      );

      await fetchDevices();
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to delete device.");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchDevices();
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokens?.access, user?.username, props.iptv_id]);

  return (
    <div className="mod">
      {/* Add device form */}
      <form onSubmit={handleAddDevice} className="mb-3">
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Device name"
            value={newDeviceName}
            onChange={(e) => setNewDeviceName(e.target.value)}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!newDeviceName.trim() || submitting}
            title="Add device"
          >
            {submitting ? "Adding…" : "Add"}
          </button>
        </div>
      </form>

      {errorMsg && (
        <div className="alert alert-danger py-2" role="alert">
          {errorMsg}
        </div>
      )}

      {/* Devices table */}
      <table className="table">
        <thead>
          <tr>
            <th>Device Name</th>
            <th>Creation Date</th>
            <th style={{ width: 120 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={3}>Loading…</td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={3}>No devices found</td>
            </tr>
          ) : (
            rows.map((item, idx) => (
              <tr key={(item.iptv_subscription_device_id ?? "dev") + "-" + idx}>
                <td>{item.device_name ?? "-"}</td>
                <td>{formatDMY(item.creation_date)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() =>
                      handleDelete(item.iptv_subscription_device_id)
                    }
                    disabled={
                      deletingId !== null &&
                      deletingId === item.iptv_subscription_device_id
                    }
                    title="Delete device"
                  >
                    {deletingId === item.iptv_subscription_device_id
                      ? "Deleting…"
                      : "Delete"}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default IPTVSubDevices;
